// TimelineWebApp/src/telemetry/trackEvent.ts
import { invokeEdgeFn } from '@/api/supabaseClient';
import type { AppEvent, QueuedEvent } from './eventCatalog';
import { useTelemetryStore } from './telemetryStore';

const FLUSH_DEBOUNCE_MS = 1000;
const RETRY_DELAY_MS = 2000;
const PRE_DEVICE_ID_BUFFER_LIMIT = 10;
// 2026-09-21 (frozen-CTA fix) - launch-path handlers `await forceFlush()` before calling
// sendToNative, so it must never gate the UI on a slow/hung server. Ceiling for the race in
// forceFlush() below; spec calls for <= 1.5s.
const FORCE_FLUSH_CEILING_MS = 1500;

function getCurrentViewContext(): string {
  if (typeof window === 'undefined') return 'unknown';
  const page = new URLSearchParams(window.location.search).get('page');
  return page || 'timeline';
}

/**
 * Emit a typed analytics event. The compiler enforces that `metadata`
 * matches the declared shape for the given `event_type`.
 *
 * Events are batched for up to 1 second, then flushed to the
 * events-ingest edge function. If the network call fails, we retry once
 * after 2 s; a second failure re-queues the batch for the next flush
 * rather than dropping it (2026-09-21). No persistent offline queue.
 *
 * If called before `setDeviceId` has been invoked, the event is held
 * in a bounded 10-slot buffer and moved to the main queue once the
 * device id lands. Beyond 10 buffered events, new events are dropped
 * with a single `console.warn`.
 */
export function trackEvent<T extends AppEvent>(
  event_type: T['event_type'],
  metadata: T['metadata'],
): void {
  const s = useTelemetryStore.getState();
  // Stamp the entitlement the user had AT THIS MOMENT. Events buffered before the
  // handshake are stamped at flush instead (see flushNow), so a null here is not final.
  const stamped: Record<string, unknown> = s.entitlement
    ? { ...(metadata as Record<string, unknown>), entitlement: s.entitlement }
    : (metadata as Record<string, unknown>);
  const queued: QueuedEvent = {
    event_type,
    view_context: getCurrentViewContext(),
    metadata: stamped,
    client_ts: new Date().toISOString(),
  };

  if (!s.deviceId) {
    if (s.preDeviceIdBuffer.length >= PRE_DEVICE_ID_BUFFER_LIMIT) {
      console.warn('[telemetry] pre-deviceId buffer full; dropping event', event_type);
      return;
    }
    useTelemetryStore.setState({ preDeviceIdBuffer: [...s.preDeviceIdBuffer, queued] });
    return;
  }

  useTelemetryStore.setState({ queue: [...s.queue, queued] });
  scheduleFlush();
}

export function scheduleFlush(): void {
  const s = useTelemetryStore.getState();
  if (s.flushTimer != null) return;
  const timer = window.setTimeout(() => { void flushNow(); }, FLUSH_DEBOUNCE_MS);
  useTelemetryStore.setState({ flushTimer: timer });
}

/**
 * Force the queue to flush immediately, bypassing the 1s debounce. Use
 * on terminal events (timeline_dismissed, welcome_choice, etc.) where
 * the WebView may be torn down by Unity before the next debounce tick.
 * Returns a Promise that resolves when the in-flight POST completes
 * (or fails). Callers can `await` it to ensure events arrive before
 * triggering a `sendToNative` that closes the WebView.
 *
 * 2026-09-21 (frozen-CTA fix) - this used to await the network call with no ceiling, so a hung
 * server froze every launch-path CTA that calls `await forceFlush()` before its native call, with
 * no spinner shown (the busy state wasn't even set yet at that point — see the callers). It now
 * races `flushNow()` against FORCE_FLUSH_CEILING_MS and never rejects. If the ceiling wins, the
 * flush keeps running in the background; a failure there re-queues the events (see flushNow's
 * catch below) instead of dropping them, so a caller that moved on doesn't cost us the data.
 */
export function forceFlush(): Promise<void> {
  const { flushTimer } = useTelemetryStore.getState();
  if (flushTimer != null) {
    window.clearTimeout(flushTimer);
    useTelemetryStore.setState({ flushTimer: null });
  }

  const flushPromise = flushNow();

  return new Promise((resolve) => {
    let settled = false;
    const ceiling = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve();
    }, FORCE_FLUSH_CEILING_MS);

    flushPromise.then(() => {
      if (settled) return;
      settled = true;
      window.clearTimeout(ceiling);
      resolve();
    });
  });
}

async function flushNow(): Promise<void> {
  const { queue, deviceId, sessionId, funnelId, entitlement } = useTelemetryStore.getState();
  useTelemetryStore.setState({ queue: [], flushTimer: null });

  if (!deviceId || queue.length === 0) return;

  // Backfill entitlement on events that were queued before the handshake landed
  // (they went into the pre-deviceId buffer with no stamp). Events that already
  // carry one keep it: it is the value at the time they fired.
  if (entitlement) {
    for (const ev of queue) {
      if (ev.metadata.entitlement === undefined) ev.metadata = { ...ev.metadata, entitlement };
    }
  }

  // funnel_id is optional in the wire protocol — backend stores null when
  // absent (standalone web has no Unity host minting one). Sample at flush
  // time, not enqueue time, so events queued before setFunnelId arrives
  // still pick up the id once it lands. If the timing is reversed (events
  // flushed before setFunnelId), they go in funnel-less and a small head
  // of the funnel is missed; acceptable since setFunnelId is sent on the
  // same `ready` cycle as setDeviceId.
  const payload = { device_id: deviceId, session_id: sessionId, funnel_id: funnelId, events: queue };

  try {
    await invokeEdgeFn('events-ingest', payload);
  } catch {
    window.setTimeout(() => {
      invokeEdgeFn('events-ingest', payload).catch(() => {
        // 2026-09-21 (frozen-CTA fix) - a second failure no longer drops the batch. forceFlush()
        // only waits up to its own short ceiling, so a caller may already have moved on by the
        // time we get here; put these events back ahead of anything queued since, so the next
        // trackEvent()/scheduleFlush() or forceFlush() picks them up. Deliberately does NOT
        // re-arm a timer itself — a server that is genuinely down would otherwise retry forever.
        const current = useTelemetryStore.getState().queue;
        useTelemetryStore.setState({ queue: [...queue, ...current] });
      });
    }, RETRY_DELAY_MS);
  }
}
